import type {
  Contract,
  ContractClause,
  ContractTerm,
  ContractTemplate,
  ContractStatus,
  ContractType,
  DocuSignStep,
} from '../types';
import { dataService } from './dataService';

// ============================================================================
// WARNING: PERSISTENCIA CON localStorage - PARA DEMO SOLAMENTE
// ============================================================================
// Este módulo usa localStorage para persistir Contratos entre recargas.
// ESTO NO ES ADECUADO PARA PRODUCCIÓN por:
//   - Limitado a 5-10MB por dominio
//   - No sincroniza entre usuarios/dispositivos
//   - No hay validación server-side
//   - No hay backup automático
//   - Datos se pierden si el usuario limpia el navegador
//
// EN PRODUCCIÓN, se necesita implementar:
//   1. POST   /api/contracts                 - Crear nuevo contrato
//   2. GET    /api/contracts                 - Listar todos los contratos
//   3. GET    /api/contracts/:id             - Detalle de un contrato
//   4. PUT    /api/contracts/:id             - Actualizar contrato
//   5. PATCH  /api/contracts/:id/send-sign   - Enviar a DocuSign
//   6. PATCH  /api/contracts/:id/cancel      - Cancelar contrato
//   7. DELETE /api/contracts/:id             - Eliminar contrato (solo borrador)
//   8. POST   /api/contracts/:id/docusign    - Webhook DocuSign
//
// Consideraciones adicionales:
//   - Timestamps deben generarse en el servidor
//   - Integración real con DocuSign eSignature API
//   - Validar permisos de usuario
//   - Implementar auditoría de cambios
//   - Notificaciones automáticas (vencimientos, firmas pendientes)
//   - Generación real de PDF con template corporativo
//   - Integración con módulo de evaluación y RFQ
//
// ⚠️ NO USAR EN PRODUCCIÓN SIN MIGRAR A BACKEND CORPORATIVO
// ============================================================================

const STORAGE_KEY = 'interbarge_contracts';

// ============================================================================
// Template de cláusulas estándar para servicios marítimos
// ============================================================================

export const STANDARD_CLAUSES: ContractClause[] = [
  {
    id: 'clause-obj',
    title: 'Objeto del Contrato',
    content: 'El presente contrato tiene por objeto establecer los términos y condiciones bajo los cuales EL PROVEEDOR proveerá los bienes y/o servicios descritos en el Anexo A a INTERBARGE S.A., empresa dedicada a operaciones de remolque marítimo y fluvial.',
    required: true,
  },
  {
    id: 'clause-duration',
    title: 'Plazo y Vigencia',
    content: 'El presente contrato tendrá vigencia desde la fecha de firma por ambas partes y hasta la fecha de vencimiento estipulada. Podrá renovarse por períodos iguales mediante acuerdo escrito de ambas partes con un preaviso mínimo de 30 (treinta) días antes del vencimiento.',
    required: true,
  },
  {
    id: 'clause-price',
    title: 'Precio y Forma de Pago',
    content: 'Los precios acordados serán los detallados en el Anexo B del presente contrato, expresados en Dólares Estadounidenses (USD). Los pagos se realizarán a los 30 (treinta) días de la fecha de emisión de la factura correspondiente, mediante transferencia bancaria a la cuenta designada por EL PROVEEDOR.',
    required: true,
  },
  {
    id: 'clause-delivery',
    title: 'Entregas y Logística',
    content: 'EL PROVEEDOR se compromete a entregar los bienes en las ubicaciones designadas por INTERBARGE (remolcadores, oficinas o depósitos), conforme a los plazos establecidos en cada orden de compra. Las entregas a embarcaciones deberán coordinarse con el Departamento de Operaciones con un mínimo de 48 horas de anticipación.',
    required: true,
  },
  {
    id: 'clause-quality',
    title: 'Calidad y Especificaciones',
    content: 'Todos los bienes y servicios provistos deberán cumplir con las especificaciones técnicas detalladas en el Anexo A y con las normas aplicables de la industria marítima (SOLAS, IMO, clasificadoras). EL PROVEEDOR garantiza que los productos son nuevos, de primera calidad y aptos para uso marítimo.',
    required: true,
  },
  {
    id: 'clause-warranty',
    title: 'Garantía',
    content: 'EL PROVEEDOR garantiza los bienes y servicios provistos por un período mínimo de 12 (doce) meses a partir de la entrega efectiva. Durante el período de garantía, cualquier defecto de fabricación, material o funcionamiento será reparado o reemplazado sin costo para INTERBARGE.',
    required: true,
  },
  {
    id: 'clause-insurance',
    title: 'Seguros',
    content: 'EL PROVEEDOR deberá mantener vigentes durante toda la duración del contrato las siguientes coberturas de seguros: (a) Responsabilidad Civil General por un mínimo de USD 1.000.000; (b) Seguro de Transporte que cubra los bienes hasta el punto de entrega; (c) ART/Seguro laboral para todo su personal.',
    required: true,
  },
  {
    id: 'clause-indemnity',
    title: 'Indemnización',
    content: 'EL PROVEEDOR se obliga a indemnizar y mantener indemne a INTERBARGE, sus directores, empleados y agentes, de cualquier reclamo, daño, pérdida o gasto (incluyendo honorarios legales) que surja directa o indirectamente del incumplimiento del presente contrato o de la negligencia del PROVEEDOR, sus empleados o subcontratistas.',
    required: true,
  },
  {
    id: 'clause-penalties',
    title: 'Penalidades por Incumplimiento',
    content: 'En caso de atraso en las entregas, INTERBARGE podrá aplicar una penalidad equivalente al 1% (uno por ciento) del valor de la orden afectada por cada día de atraso, hasta un máximo del 10% (diez por ciento). Si el atraso supera los 15 (quince) días, INTERBARGE podrá resolver el contrato sin obligación de indemnización.',
    required: false,
  },
  {
    id: 'clause-sla',
    title: 'Nivel de Servicio (SLA)',
    content: 'EL PROVEEDOR se compromete a mantener los siguientes niveles de servicio mínimos: (a) Tiempo de respuesta a solicitudes: máximo 24 horas hábiles; (b) Disponibilidad de stock para items críticos: mínimo 95%; (c) Tasa de entrega a tiempo: mínimo 90%; (d) Tasa de productos defectuosos: máximo 2%.',
    required: false,
  },
  {
    id: 'clause-safety',
    title: 'Seguridad y Medio Ambiente',
    content: 'EL PROVEEDOR deberá cumplir con todas las normas de seguridad marítima y medioambiental aplicables, incluyendo las regulaciones de la Prefectura Naval Argentina (PNA), SOLAS, MARPOL y normativa ambiental local. Todo personal del PROVEEDOR que acceda a embarcaciones deberá contar con certificaciones de seguridad vigentes.',
    required: true,
  },
  {
    id: 'clause-confidentiality',
    title: 'Confidencialidad',
    content: 'Las partes se comprometen a mantener en estricta confidencialidad toda información comercial, técnica y financiera intercambiada con motivo del presente contrato. Esta obligación se mantendrá vigente por un período de 2 (dos) años posteriores a la terminación del contrato.',
    required: false,
  },
  {
    id: 'clause-termination',
    title: 'Resolución Anticipada',
    content: 'Cualquiera de las partes podrá resolver anticipadamente el presente contrato mediante notificación fehaciente con un preaviso de 60 (sesenta) días. En caso de incumplimiento grave, la parte afectada podrá resolver el contrato de pleno derecho, sin necesidad de preaviso, mediante notificación fehaciente.',
    required: true,
  },
  {
    id: 'clause-jurisdiction',
    title: 'Jurisdicción y Ley Aplicable',
    content: 'El presente contrato se regirá por las leyes de la República Argentina. Para cualquier controversia que pudiera surgir, las partes se someten a la jurisdicción de los Tribunales Ordinarios en lo Comercial de la Ciudad Autónoma de Buenos Aires, renunciando a cualquier otro fuero o jurisdicción que pudiera corresponderles.',
    required: true,
  },
  {
    id: 'clause-force-majeure',
    title: 'Fuerza Mayor',
    content: 'Ninguna de las partes será responsable por el incumplimiento de sus obligaciones cuando dicho incumplimiento se deba a causas de fuerza mayor o caso fortuito, tales como guerras, terremotos, inundaciones, pandemias, huelgas generales, restricciones gubernamentales o cualquier otro evento fuera del control razonable de la parte afectada.',
    required: false,
  },
];

export const STANDARD_TERMS: ContractTerm[] = [
  { id: 'term-payment', description: 'Condición de pago', value: '30 días desde factura' },
  { id: 'term-delivery', description: 'Plazo máximo de entrega', value: '15 días hábiles' },
  { id: 'term-warranty', description: 'Período de garantía', value: '12 meses' },
  { id: 'term-penalty', description: 'Penalidad por atraso', value: '1% diario, máx. 10%' },
  { id: 'term-sla-response', description: 'Tiempo respuesta solicitudes', value: '24 horas hábiles' },
  { id: 'term-sla-stock', description: 'Disponibilidad stock crítico', value: '≥ 95%' },
  { id: 'term-insurance', description: 'Seguro RC mínimo', value: 'USD 1.000.000' },
  { id: 'term-preaviso', description: 'Preaviso terminación', value: '60 días' },
];

export const CONTRACT_TEMPLATE: ContractTemplate = {
  clauses: STANDARD_CLAUSES,
  terms: STANDARD_TERMS,
};

// ============================================================================
// Colores y labels por estado
// ============================================================================

export const contractStatusColors: Record<ContractStatus, string> = {
  draft: '#9e9e9e',
  review: '#ff9800',
  sent_to_sign: '#2196f3',
  signed: '#4caf50',
  expired: '#f44336',
  cancelled: '#616161',
};

export const contractStatusLabels: Record<ContractStatus, string> = {
  draft: 'Borrador',
  review: 'En Revisión',
  sent_to_sign: 'Pendiente Firma',
  signed: 'Firmado',
  expired: 'Vencido',
  cancelled: 'Cancelado',
};

export const contractTypeLabels: Record<ContractType, string> = {
  service: 'Servicio',
  supply: 'Suministro',
  framework: 'Contrato Marco',
};

export const docusignStepLabels: Record<DocuSignStep, string> = {
  draft: 'Borrador',
  sent: 'Enviado',
  viewed: 'Visto',
  signed: 'Firmado',
};

// ============================================================================
// Contract Service
// ============================================================================

class ContractService {
  // =========================================================================
  // CRUD Operations
  // =========================================================================

  getAll(): Contract[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];
      return JSON.parse(stored) as Contract[];
    } catch (error) {
      console.error('[ContractService] Error cargando contratos:', error);
      return [];
    }
  }

  getById(id: string): Contract | null {
    const contracts = this.getAll();
    return contracts.find(c => c.id === id) || null;
  }

  create(data: {
    title: string;
    supplierId: string;
    supplierName: string;
    type: ContractType;
    startDate: string;
    endDate: string;
    value: number;
    currency?: string;
    terms: ContractTerm[];
    clauses: ContractClause[];
    notes?: string;
    createdBy?: string;
  }): Contract {
    const contracts = this.getAll();
    const now = new Date().toISOString();

    const newContract: Contract = {
      id: crypto.randomUUID(),
      contractNumber: this.getNextNumber(),
      title: data.title,
      supplierId: data.supplierId,
      supplierName: data.supplierName,
      type: data.type,
      status: 'draft',
      startDate: data.startDate,
      endDate: data.endDate,
      value: data.value,
      currency: data.currency || 'USD',
      terms: data.terms,
      clauses: data.clauses,
      history: [
        {
          id: crypto.randomUUID(),
          action: 'Contrato creado',
          date: now,
          user: data.createdBy || 'Demo User',
          details: `Contrato ${data.type === 'service' ? 'de servicio' : data.type === 'supply' ? 'de suministro' : 'marco'} con ${data.supplierName}`,
        },
      ],
      createdBy: data.createdBy || 'Demo User',
      createdAt: now,
      updatedAt: now,
      notes: data.notes,
    };

    contracts.push(newContract);
    this.saveAll(contracts);
    console.log(`[ContractService] Contrato creado: ${newContract.contractNumber} - ${newContract.title}`);
    return newContract;
  }

  update(id: string, data: Partial<Contract>): Contract {
    const contracts = this.getAll();
    const index = contracts.findIndex(c => c.id === id);
    if (index === -1) throw new Error(`Contrato ${id} no encontrado`);

    contracts[index] = {
      ...contracts[index],
      ...data,
      updatedAt: new Date().toISOString(),
    };
    this.saveAll(contracts);
    console.log(`[ContractService] Contrato actualizado: ${contracts[index].contractNumber}`);
    return contracts[index];
  }

  delete(id: string): void {
    const contracts = this.getAll();
    const filtered = contracts.filter(c => c.id !== id);
    if (filtered.length === contracts.length) throw new Error(`Contrato ${id} no encontrado`);
    this.saveAll(filtered);
    console.log(`[ContractService] Contrato eliminado: ${id}`);
  }

  // =========================================================================
  // Workflow / Estado
  // =========================================================================

  sendToReview(id: string): Contract {
    const contracts = this.getAll();
    const index = contracts.findIndex(c => c.id === id);
    if (index === -1) throw new Error(`Contrato ${id} no encontrado`);

    const now = new Date().toISOString();
    contracts[index].status = 'review';
    contracts[index].updatedAt = now;
    contracts[index].history.push({
      id: crypto.randomUUID(),
      action: 'Enviado a revisión',
      date: now,
      user: 'Demo User',
      details: 'Contrato enviado para revisión interna antes de firma',
    });

    this.saveAll(contracts);
    return contracts[index];
  }

  sendToDocuSign(id: string): Contract {
    const contracts = this.getAll();
    const index = contracts.findIndex(c => c.id === id);
    if (index === -1) throw new Error(`Contrato ${id} no encontrado`);

    const now = new Date().toISOString();
    const envelopeId = `ENV-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;

    contracts[index].status = 'sent_to_sign';
    contracts[index].updatedAt = now;
    contracts[index].docusignEnvelopeId = envelopeId;
    contracts[index].docusignStep = 'sent';
    contracts[index].docusignSentAt = now;
    contracts[index].history.push({
      id: crypto.randomUUID(),
      action: 'Enviado a DocuSign',
      date: now,
      user: 'Demo User',
      details: `Envelope ID: ${envelopeId} — Enviado al proveedor ${contracts[index].supplierName} para firma electrónica`,
    });

    this.saveAll(contracts);
    return contracts[index];
  }

  simulateDocuSignView(id: string): Contract {
    const contracts = this.getAll();
    const index = contracts.findIndex(c => c.id === id);
    if (index === -1) throw new Error(`Contrato ${id} no encontrado`);

    const now = new Date().toISOString();
    contracts[index].docusignStep = 'viewed';
    contracts[index].docusignViewedAt = now;
    contracts[index].updatedAt = now;
    contracts[index].history.push({
      id: crypto.randomUUID(),
      action: 'Documento visto por el proveedor',
      date: now,
      user: contracts[index].supplierName,
      details: `El proveedor abrió el documento en DocuSign (Envelope: ${contracts[index].docusignEnvelopeId})`,
    });

    this.saveAll(contracts);
    return contracts[index];
  }

  simulateDocuSignSign(id: string): Contract {
    const contracts = this.getAll();
    const index = contracts.findIndex(c => c.id === id);
    if (index === -1) throw new Error(`Contrato ${id} no encontrado`);

    const now = new Date().toISOString();
    contracts[index].status = 'signed';
    contracts[index].docusignStep = 'signed';
    contracts[index].docusignSignedAt = now;
    contracts[index].signedAt = now;
    contracts[index].updatedAt = now;
    contracts[index].history.push({
      id: crypto.randomUUID(),
      action: 'Contrato firmado electrónicamente',
      date: now,
      user: contracts[index].supplierName,
      details: `Firma completada en DocuSign (Envelope: ${contracts[index].docusignEnvelopeId}). Contrato vigente hasta ${contracts[index].endDate}.`,
    });

    this.saveAll(contracts);
    return contracts[index];
  }

  cancel(id: string, reason?: string): Contract {
    const contracts = this.getAll();
    const index = contracts.findIndex(c => c.id === id);
    if (index === -1) throw new Error(`Contrato ${id} no encontrado`);

    const now = new Date().toISOString();
    contracts[index].status = 'cancelled';
    contracts[index].updatedAt = now;
    contracts[index].history.push({
      id: crypto.randomUUID(),
      action: 'Contrato cancelado',
      date: now,
      user: 'Demo User',
      details: reason || 'Cancelado por decisión interna',
    });

    this.saveAll(contracts);
    return contracts[index];
  }

  // =========================================================================
  // Helpers
  // =========================================================================

  getNextNumber(): string {
    const contracts = this.getAll();
    const maxNum = contracts.reduce((max, c) => {
      const num = parseInt(c.contractNumber.replace('CTR-', ''), 10);
      return isNaN(num) ? max : Math.max(max, num);
    }, 0);
    return `CTR-${String(maxNum + 1).padStart(3, '0')}`;
  }

  getTemplate(): ContractTemplate {
    return CONTRACT_TEMPLATE;
  }

  private saveAll(contracts: Contract[]): void {
    try {
      const jsonString = JSON.stringify(contracts);
      localStorage.setItem(STORAGE_KEY, jsonString);
      console.log(`[ContractService] Guardado exitoso:`, {
        key: STORAGE_KEY,
        totalContracts: contracts.length,
        sizeKB: (jsonString.length / 1024).toFixed(2),
      });

      // Disparar evento custom para notificar a otros componentes (Dashboard, etc.)
      window.dispatchEvent(new Event('contractsUpdated'));
    } catch (error) {
      console.error('[ContractService] ERROR al guardar contratos:', error);
    }
  }

  // =========================================================================
  // Inicialización con mock data
  // =========================================================================

  initializeIfEmpty(): void {
    const existing = this.getAll();
    if (existing.length > 0) {
      console.log(`[ContractService] Ya hay ${existing.length} contratos en localStorage`);
      return;
    }

    console.log('[ContractService] localStorage vacío, generando mock data...');
    const mockContracts = this.generateMockContracts();
    this.saveAll(mockContracts);
    console.log(`[ContractService] Mock data generado: ${mockContracts.length} contratos`);
  }

  private generateMockContracts(): Contract[] {
    const allSuppliers = dataService.getSuppliers();
    const supplierNames = allSuppliers.length > 10
      ? allSuppliers.slice(0, 10)
      : ['Movicor SRL', 'Marine Parts Co', 'Navales del Sur', 'Hidráulica Litoral', 'Motores BAI'];

    const makeSupplierId = (name: string) => `supplier-${name.toLowerCase().replace(/\s+/g, '-')}`;

    const contracts: Contract[] = [];

    // Contrato 1: Firmado - Suministro de lubricantes (vigente)
    contracts.push({
      id: crypto.randomUUID(),
      contractNumber: 'CTR-001',
      title: 'Suministro Anual de Lubricantes Marinos',
      supplierId: makeSupplierId(supplierNames[0]),
      supplierName: supplierNames[0],
      type: 'supply',
      status: 'signed',
      startDate: '2026-01-01',
      endDate: '2026-12-31',
      value: 185000,
      currency: 'USD',
      terms: [
        { id: 't1', description: 'Condición de pago', value: '30 días' },
        { id: 't2', description: 'Plazo de entrega', value: '10 días hábiles' },
        { id: 't3', description: 'Garantía', value: '12 meses' },
        { id: 't4', description: 'Descuento por volumen', value: '5% sobre USD 50.000' },
      ],
      clauses: STANDARD_CLAUSES.filter(c => c.required).map(c => ({ ...c, id: crypto.randomUUID() })),
      history: [
        { id: crypto.randomUUID(), action: 'Contrato creado', date: '2025-11-15T10:00:00Z', user: 'Demo User', details: 'Contrato de suministro anual de lubricantes' },
        { id: crypto.randomUUID(), action: 'Enviado a revisión', date: '2025-11-20T14:00:00Z', user: 'Demo User', details: 'Revisión por departamento legal' },
        { id: crypto.randomUUID(), action: 'Enviado a DocuSign', date: '2025-12-01T09:00:00Z', user: 'Demo User', details: `Envelope ID: ENV-MOCK001` },
        { id: crypto.randomUUID(), action: 'Documento visto por el proveedor', date: '2025-12-02T11:00:00Z', user: supplierNames[0] },
        { id: crypto.randomUUID(), action: 'Contrato firmado electrónicamente', date: '2025-12-05T16:30:00Z', user: supplierNames[0], details: 'Firma completada. Contrato vigente desde 01/01/2026.' },
      ],
      docusignEnvelopeId: 'ENV-MOCK001',
      docusignStep: 'signed',
      docusignSentAt: '2025-12-01T09:00:00Z',
      docusignViewedAt: '2025-12-02T11:00:00Z',
      docusignSignedAt: '2025-12-05T16:30:00Z',
      createdBy: 'Demo User',
      createdAt: '2025-11-15T10:00:00Z',
      updatedAt: '2025-12-05T16:30:00Z',
      signedAt: '2025-12-05T16:30:00Z',
    });

    // Contrato 2: Enviado a firma - Servicio de mantenimiento
    contracts.push({
      id: crypto.randomUUID(),
      contractNumber: 'CTR-002',
      title: 'Servicio de Mantenimiento Motores Principales',
      supplierId: makeSupplierId(supplierNames[1]),
      supplierName: supplierNames[1],
      type: 'service',
      status: 'sent_to_sign',
      startDate: '2026-04-01',
      endDate: '2027-03-31',
      value: 320000,
      currency: 'USD',
      terms: [
        { id: 't1', description: 'Condición de pago', value: '60 días' },
        { id: 't2', description: 'Tiempo de respuesta', value: '4 horas (emergencia)' },
        { id: 't3', description: 'Garantía trabajos', value: '24 meses' },
        { id: 't4', description: 'Personal técnico mínimo', value: '2 técnicos certificados CAT' },
      ],
      clauses: STANDARD_CLAUSES.map(c => ({ ...c, id: crypto.randomUUID() })),
      history: [
        { id: crypto.randomUUID(), action: 'Contrato creado', date: '2026-03-01T08:00:00Z', user: 'Demo User', details: 'Servicio integral de mantenimiento de motores Caterpillar' },
        { id: crypto.randomUUID(), action: 'Enviado a revisión', date: '2026-03-05T10:00:00Z', user: 'Demo User' },
        { id: crypto.randomUUID(), action: 'Enviado a DocuSign', date: '2026-03-15T14:00:00Z', user: 'Demo User', details: 'Envelope ID: ENV-MOCK002' },
        { id: crypto.randomUUID(), action: 'Documento visto por el proveedor', date: '2026-03-16T09:00:00Z', user: supplierNames[1] },
      ],
      docusignEnvelopeId: 'ENV-MOCK002',
      docusignStep: 'viewed',
      docusignSentAt: '2026-03-15T14:00:00Z',
      docusignViewedAt: '2026-03-16T09:00:00Z',
      createdBy: 'Demo User',
      createdAt: '2026-03-01T08:00:00Z',
      updatedAt: '2026-03-16T09:00:00Z',
    });

    // Contrato 3: En revisión - Framework de repuestos
    contracts.push({
      id: crypto.randomUUID(),
      contractNumber: 'CTR-003',
      title: 'Acuerdo Marco - Repuestos y Accesorios Marinos',
      supplierId: makeSupplierId(supplierNames[2]),
      supplierName: supplierNames[2],
      type: 'framework',
      status: 'review',
      startDate: '2026-05-01',
      endDate: '2028-04-30',
      value: 500000,
      currency: 'USD',
      terms: [
        { id: 't1', description: 'Condición de pago', value: '30 días' },
        { id: 't2', description: 'Plazo de entrega estándar', value: '15 días hábiles' },
        { id: 't3', description: 'Plazo urgente', value: '48 horas (costo +15%)' },
        { id: 't4', description: 'Vigencia acuerdo', value: '2 años renovable' },
      ],
      clauses: STANDARD_CLAUSES.map(c => ({ ...c, id: crypto.randomUUID() })),
      history: [
        { id: crypto.randomUUID(), action: 'Contrato creado', date: '2026-04-01T10:00:00Z', user: 'Demo User', details: 'Acuerdo marco para provisión de repuestos marinos' },
        { id: crypto.randomUUID(), action: 'Enviado a revisión', date: '2026-04-05T11:00:00Z', user: 'Demo User', details: 'Pendiente aprobación por Gerencia de Compras' },
      ],
      createdBy: 'Demo User',
      createdAt: '2026-04-01T10:00:00Z',
      updatedAt: '2026-04-05T11:00:00Z',
      notes: 'Acuerdo marco a 2 años. Pendiente definición de lista de precios por familia de producto.',
    });

    // Contrato 4: Borrador - Equipos de seguridad
    contracts.push({
      id: crypto.randomUUID(),
      contractNumber: 'CTR-004',
      title: 'Suministro de Equipos de Seguridad Marítima',
      supplierId: makeSupplierId(supplierNames[3]),
      supplierName: supplierNames[3],
      type: 'supply',
      status: 'draft',
      startDate: '2026-06-01',
      endDate: '2027-05-31',
      value: 95000,
      currency: 'USD',
      terms: STANDARD_TERMS.map(t => ({ ...t, id: crypto.randomUUID() })),
      clauses: STANDARD_CLAUSES.filter(c => c.required).map(c => ({ ...c, id: crypto.randomUUID() })),
      history: [
        { id: crypto.randomUUID(), action: 'Contrato creado', date: '2026-04-10T09:00:00Z', user: 'Demo User', details: 'Borrador para suministro de chalecos, aros, extintores y equipos SOLAS' },
      ],
      createdBy: 'Demo User',
      createdAt: '2026-04-10T09:00:00Z',
      updatedAt: '2026-04-10T09:00:00Z',
      notes: 'Renovación obligatoria por normativa PNA. Requiere certificaciones SOLAS actualizadas.',
    });

    // Contrato 5: Vencido - Contrato del año pasado
    contracts.push({
      id: crypto.randomUUID(),
      contractNumber: 'CTR-005',
      title: 'Servicio de Pintura y Carenado 2025',
      supplierId: makeSupplierId(supplierNames[4]),
      supplierName: supplierNames[4],
      type: 'service',
      status: 'expired',
      startDate: '2025-01-01',
      endDate: '2025-12-31',
      value: 210000,
      currency: 'USD',
      terms: [
        { id: 't1', description: 'Condición de pago', value: '30 días' },
        { id: 't2', description: 'Garantía trabajos', value: '18 meses' },
        { id: 't3', description: 'Plazo carenado completo', value: '21 días por embarcación' },
      ],
      clauses: STANDARD_CLAUSES.filter(c => c.required).map(c => ({ ...c, id: crypto.randomUUID() })),
      history: [
        { id: crypto.randomUUID(), action: 'Contrato creado', date: '2024-11-10T10:00:00Z', user: 'Demo User' },
        { id: crypto.randomUUID(), action: 'Contrato firmado electrónicamente', date: '2024-12-01T16:00:00Z', user: supplierNames[4] },
        { id: crypto.randomUUID(), action: 'Contrato vencido', date: '2025-12-31T23:59:59Z', user: 'Sistema', details: 'Contrato alcanzó fecha de vencimiento. Pendiente decisión de renovación.' },
      ],
      docusignEnvelopeId: 'ENV-MOCK005',
      docusignStep: 'signed',
      docusignSentAt: '2024-11-20T09:00:00Z',
      docusignSignedAt: '2024-12-01T16:00:00Z',
      createdBy: 'Demo User',
      createdAt: '2024-11-10T10:00:00Z',
      updatedAt: '2025-12-31T23:59:59Z',
      signedAt: '2024-12-01T16:00:00Z',
    });

    return contracts;
  }
}

// Singleton instance
export const contractService = new ContractService();
